import { useQuery } from '@tanstack/react-query';
import { Gift } from 'lucide-react';
import { toast } from 'sonner';

import { copyToClipboard } from '@nesso/ui/lib/clipboard';
import { Button } from '@nesso/ui/primitives/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@nesso/ui/primitives/dialog';
import {
    Item,
    ItemContent,
    ItemDescription,
    ItemMedia,
    ItemTitle,
} from '@nesso/ui/primitives/item';
import { Skeleton } from '@nesso/ui/primitives/skeleton';

import { referralLinkOptions } from '@/entities/referral';

const REFERRAL_TERMS_URL = 'https://www.nessoapp.com/terms';

type Props = {
    open: boolean;
    onOpenChange: (open: boolean) => void;
};

export function ShareReferralLinkDialog({ open, onOpenChange }: Props) {
    const {
        data: referral,
        isPending,
        isError,
        refetch,
        isFetching,
    } = useQuery(referralLinkOptions(open));
    const referralLink = referral?.link;

    const copy = async () => {
        if (!referralLink) return;

        const copied = await copyToClipboard(referralLink);
        if (copied) {
            toast.success('Copied to clipboard');
        } else {
            toast.error('Could not copy the link — please try again');
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent>
                <DialogHeader className="gap-1.5 mb-5">
                    <DialogTitle className="text-left">
                        Share Nesso and earn a free month
                    </DialogTitle>
                    <DialogDescription className="text-left font-medium">
                        When someone signs up with a paid plan via your referral link, they get a
                        free month and so do you!
                    </DialogDescription>
                </DialogHeader>
                <Item variant="outline" alignment="center" className="min-w-0">
                    <ItemMedia
                        variant="icon"
                        className="bg-secondary-background border-secondary-border self-center!"
                    >
                        <Gift className="text-secondary-text" />
                    </ItemMedia>
                    <ItemContent className="min-w-0">
                        <ItemTitle>Referral Link</ItemTitle>
                        {isPending && <Skeleton className="h-5 w-full" />}
                        {isError && (
                            <ItemDescription>
                                Your link isn&rsquo;t ready yet. Try again in a moment.
                            </ItemDescription>
                        )}
                        {referralLink && (
                            <ItemDescription className="h-5 leading-none block truncate">
                                {referralLink}
                            </ItemDescription>
                        )}
                    </ItemContent>
                </Item>
                <DialogFooter className="flex-col sm:flex-col gap-4">
                    {isError ? (
                        <Button onClick={() => void refetch()} disabled={isFetching}>
                            Try Again
                        </Button>
                    ) : (
                        <Button onClick={copy} disabled={!referralLink}>
                            Copy Link
                        </Button>
                    )}
                    <Button
                        variant="link"
                        size="sm"
                        className="h-8"
                        onClick={() =>
                            window.open(REFERRAL_TERMS_URL, '_blank', 'noopener,noreferrer')
                        }
                    >
                        Referral Program Terms
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
